export type FrappeFilterOperator =
  | "="
  | "!="
  | ">"
  | ">="
  | "<"
  | "<="
  | "like"
  | "not like"
  | "in"
  | "not in"
  | "between"
  | "is";

export type FrappeConditionOperator =
  | FrappeFilterOperator
  | "within"
  | "timespan"
  | "fiscal_year";

export type FrappeFilterValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean | null>;

export type FrappeFilterClause =
  | [field: string, operator: FrappeFilterOperator, value: FrappeFilterValue]
  | [
      doctype: string,
      field: string,
      operator: FrappeFilterOperator,
      value: FrappeFilterValue,
    ];

export interface FrappeListFilterCondition {
  id: string;
  field: string;
  operator: FrappeConditionOperator;
  value: FrappeFilterValue | undefined;
  displayLabel?: string;
  doctype?: string;
  group?: "filters" | "or_filters";
}

interface BuildFrappeFilterInput {
  field: string;
  operator: FrappeFilterOperator;
  value: FrappeFilterValue;
  doctype?: string;
}

export function buildFrappeFilter({
  field,
  operator,
  value,
  doctype,
}: BuildFrappeFilterInput): FrappeFilterClause {
  if (doctype)
    return [doctype, field, operator, value];

  return [field, operator, value];
}

export function buildFrappeFilters(
  filters: Array<BuildFrappeFilterInput | null | undefined | false>,
): FrappeFilterClause[] {
  return filters
    .filter(Boolean)
    .map(filter => buildFrappeFilter(filter as BuildFrappeFilterInput));
}

/**
 * Makes typed text match LITERALLY inside a `like` pattern. MariaDB's LIKE treats `\` as its
 * escape character and `%` / `_` as wildcards, and Frappe passes the value through unchanged —
 * so "5\CV-L" searched as "5CV-L" and "a_b" matched "axb". Escape before wrapping in `%…%`.
 */
export function escapeFrappeLikeValue(value: string) {
  return value.replace(/[\\%_]/g, match => `\\${match}`);
}

export function buildFrappeLikeSearchFilters(
  fields: string[],
  search?: string | null,
  doctype?: string,
): FrappeFilterClause[] {
  const normalizedSearch = search?.trim();

  if (!normalizedSearch)
    return [];

  return fields.map(field =>
    buildFrappeFilter({
      doctype,
      field,
      operator: "like",
      value: `%${normalizedSearch}%`,
    }),
  );
}

export function serializeFrappeFilters(
  filters?: FrappeFilterClause[] | null,
): string | undefined {
  if (!filters?.length)
    return undefined;

  return JSON.stringify(filters);
}

export function toFrappeFilterParams(
  filters?: FrappeFilterClause[] | null,
  orFilters?: FrappeFilterClause[] | null,
): { filters?: string; or_filters?: string } {
  return {
    filters: serializeFrappeFilters(filters),
    or_filters: serializeFrappeFilters(orFilters),
  };
}
